/**
 * meter-qty.js
 * Handles all metre-based pricing & quantity logic for products tagged "meter_qty"
 * Logic: 1 Shopify unit = 0.25 metre | Min & Step are controlled by theme settings
 *
 * Theme settings wired in via data attributes on #meter-qty-selector:
 *   data-min-meters  — minimum metres (default 1)
 *   data-step        — step in metres  (default 0.25)
 *
 * Standard products read their min from the <input min="…"> attribute.
 */

(function () {
  'use strict';

  /* ─── Defaults (overridden per-product by data attrs) ─── */
  const UNITS_PER_METER = 4; // 1 metre = 4 Shopify units

  /* ─── Helpers ─── */
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: window.Shopify ? (window.Shopify.currency.active || 'INR') : 'INR',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  function snap(val, step) {
    return Math.round(val / step) * step;
  }

  function metersToUnits(meters) {
    return Math.round(meters * UNITS_PER_METER);
  }

  function unitsToMeters(units) {
    return units / UNITS_PER_METER;
  }

  function formatMetersDisplay(meters) {
    if (meters % 1 === 0) return meters.toFixed(0);
    const str = meters.toFixed(2);
    return str.endsWith('0') ? str.slice(0, -1) : str;
  }

  /* ════════════════════════════════════════
     PDP — Metre Quantity Selector
  ════════════════════════════════════════ */
  function initPDPMeterSelector() {
    const selector = document.getElementById('meter-qty-selector');
    const minusBtn = document.getElementById('meter-qty-minus');
    const plusBtn = document.getElementById('meter-qty-plus');
    const display = document.getElementById('meter-qty-display');
    const realInput = document.getElementById('meter-qty-real-input');
    const priceEl = document.getElementById('pdp-price');
    const totalPriceEl = document.getElementById('meter-qty-total-price');
    const totalMetersEl = document.getElementById('meter-qty-total-meters');

    if (!minusBtn || !plusBtn || !display || !realInput || !selector) return;

    // Read settings from data attributes (set by Liquid via theme settings)
    const MIN_METERS = parseFloat(selector.dataset.minMeters || '1');
    const STEP_METERS = parseFloat(selector.dataset.step || '0.25');

    let currentMeters = MIN_METERS;

    function update() {
      // Always read the latest unit price from the DOM so variant switches are reflected
      const unitPriceCents = parseInt(priceEl ? priceEl.dataset.unitPrice : 0, 10);
      display.textContent = formatMetersDisplay(currentMeters);
      realInput.value = metersToUnits(currentMeters);

      // Price per metre = unit price × UNITS_PER_METER
      const pricePerMeter = unitPriceCents * UNITS_PER_METER;

      // Total price for the currently selected metres
      const total = unitPriceCents * metersToUnits(currentMeters);

      // ── Main price: show TOTAL so it updates when metres change ──
      if (priceEl) priceEl.textContent = formatMoney(total);

      // ── Secondary label: always show per-metre rate ──
      const perMetreEl = document.getElementById('pdp-price-per-metre');
      if (perMetreEl) perMetreEl.textContent = formatMoney(pricePerMeter);

      // Legacy total elements (if present)
      if (totalPriceEl) totalPriceEl.textContent = formatMoney(total);
      if (totalMetersEl) totalMetersEl.textContent = formatMetersDisplay(currentMeters);

      // Disable minus at min
      minusBtn.disabled = currentMeters <= MIN_METERS;
      minusBtn.classList.toggle('opacity-40', currentMeters <= MIN_METERS);

      // Disable plus at max
      const maxAttr = selector.dataset.maxMeters;
      const MAX_METERS = maxAttr ? parseFloat(maxAttr) : Infinity;
      if (MAX_METERS >= MIN_METERS && currentMeters > MAX_METERS) {
        currentMeters = MAX_METERS;
      }
      if (currentMeters < MIN_METERS) {
        currentMeters = MIN_METERS;
      }
      display.textContent = formatMetersDisplay(currentMeters);
      realInput.value = metersToUnits(currentMeters);
      plusBtn.disabled = currentMeters >= MAX_METERS;
      plusBtn.classList.toggle('opacity-40', currentMeters >= MAX_METERS);

      // Update preset active styles
      document.querySelectorAll('.preset-btn').forEach(btn => {
        if (parseFloat(btn.dataset.val) === currentMeters) {
          btn.className = "preset-btn cursor-pointer px-4 py-1.5 rounded-lg border text-sm font-medium transition border-brand-orange text-brand-orange bg-brand-orange/5";
        } else {
          btn.className = "preset-btn cursor-pointer px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-sm font-medium transition hover:border-gray-300";
        }
      });
    }

    minusBtn.addEventListener('click', () => {
      const next = snap(currentMeters - STEP_METERS, STEP_METERS);
      if (next >= MIN_METERS) { currentMeters = next; update(); }
    });

    plusBtn.addEventListener('mouseenter', () => {
      const maxAttr = selector.dataset.maxMeters;
      const MAX_METERS = maxAttr ? parseFloat(maxAttr) : Infinity;
      if (currentMeters >= MAX_METERS) {
        showToast(`Only ${MAX_METERS} m available in stock.`);
      }
    });

    plusBtn.addEventListener('click', () => {
      const maxAttr = selector.dataset.maxMeters;
      const MAX_METERS = maxAttr ? parseFloat(maxAttr) : Infinity;
      if (currentMeters < MAX_METERS) {
        currentMeters = snap(currentMeters + STEP_METERS, STEP_METERS);
        update();
      } else {
        showToast(`Only ${MAX_METERS} m available in stock.`);
      }
    });

    document.querySelectorAll('.preset-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const maxAttr = selector.dataset.maxMeters;
        const MAX_METERS = maxAttr ? parseFloat(maxAttr) : Infinity;
        const requested = parseFloat(btn.dataset.val);

        if (MAX_METERS < MIN_METERS) {
          showToast(`This item is out of stock (minimum order is ${MIN_METERS} m).`);
          return;
        }

        if (requested > MAX_METERS) {
          currentMeters = Math.max(MIN_METERS, MAX_METERS);
          showToast(`Only ${MAX_METERS} m available in stock.`);
        } else {
          currentMeters = Math.max(MIN_METERS, requested);
        }
        update();
      });
    });

    update(); // initialise

    // Expose update() globally so the inline variant script can call it
    // even though this file is deferred (loaded after inline scripts execute).
    window.meterQtyRefreshPrice = update;

    // If the variant script already queued a price update before this deferred
    // script finished loading, flush it now.
    if (window._meterQtyPendingUpdate) {
      window._meterQtyPendingUpdate = false;
      update();
    }
  }

  /* ─── Standard quantity selector ─── */
  function initStdQtySelector() {
    const minus = document.getElementById('std-qty-minus');
    const plus = document.getElementById('std-qty-plus');
    const input = document.getElementById('std-qty-input');
    const hidden = document.getElementById('std-qty-hidden');
    if (!minus || !plus || !input) return;

    // Read the min from the input's own min attribute (set by theme setting in Liquid)
    const MIN_QTY = parseInt(input.min || '1', 10);

    function sync() {
      const v = parseInt(input.value, 10);
      if (hidden) hidden.value = v;
      minus.disabled = v <= MIN_QTY;
      minus.classList.toggle('opacity-40', v <= MIN_QTY);

      const maxAttr = input.getAttribute('max');
      const max = maxAttr && maxAttr !== '' ? parseInt(maxAttr, 10) : Infinity;
      plus.disabled = v >= max;
      plus.classList.toggle('opacity-40', v >= max);
    }

    minus.addEventListener('click', () => {
      const v = Math.max(MIN_QTY, parseInt(input.value, 10) - 1);
      input.value = v;
      sync();
    });
    plus.addEventListener('mouseenter', () => {
      const maxAttr = input.getAttribute('max');
      const max = maxAttr && maxAttr !== '' ? parseInt(maxAttr, 10) : Infinity;
      if (parseInt(input.value, 10) >= max) {
        showToast(`Only ${max} item(s) available in stock.`);
      }
    });

    plus.addEventListener('click', () => {
      const maxAttr = input.getAttribute('max');
      const max = maxAttr && maxAttr !== '' ? parseInt(maxAttr, 10) : Infinity;
      if (parseInt(input.value, 10) < max) {
        input.value = parseInt(input.value, 10) + 1;
        sync();
      } else {
        showToast(`Only ${max} item(s) available in stock.`);
      }
    });
    input.addEventListener('change', () => {
      let v = parseInt(input.value, 10);
      if (isNaN(v) || v < MIN_QTY) v = MIN_QTY;

      const maxAttr = input.getAttribute('max');
      const max = maxAttr && maxAttr !== '' ? parseInt(maxAttr, 10) : Infinity;
      if (v > max) {
        v = max;
        showToast(`Only ${max} item(s) available in stock.`);
      }

      input.value = v;
      sync();
    });
    sync();
  }

  /* ─── Add to Cart via AJAX ─── */
  function showToast(message) {
    const existing = document.getElementById('cart-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'cart-toast';
    toast.className = 'fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] bg-gray-900 text-white px-6 py-3.5 rounded-full shadow-2xl flex items-center gap-3 text-sm font-medium';
    toast.style.animation = 'cartToastSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
    toast.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      ${message}
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translate(-50%, 100%)';
      toast.style.transition = 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
      setTimeout(() => toast.remove(), 400);
    }, 3500);
  }

  window.showMeterToast = showToast;

  function initAjaxAddToCart() {
    const form = document.getElementById('pdp-add-to-cart-form');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = document.getElementById('pdp-add-to-cart-btn');
        if (btn && (btn.disabled || btn.dataset.available === 'false')) return;
        const variantId = document.getElementById('pdp-variant-id')?.value;
        const isMeter = document.getElementById('pdp-price')?.dataset.isMeter === 'true';
        let qty;

        if (isMeter) {
          qty = parseInt(document.getElementById('meter-qty-real-input')?.value || 4, 10);
        } else {
          const stdInput = document.getElementById('std-qty-input');
          qty = parseInt(
            document.getElementById('std-qty-hidden')?.value ||
            stdInput?.value ||
            '1',
            10
          );
        }

        if (!variantId) return;

        btn.disabled = true;
        btn.textContent = '';
        btn.insertAdjacentHTML('beforeend', '<svg class="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> Adding...');

        try {
          const body = { id: variantId, quantity: qty };
          if (isMeter) {
            body.properties = {
              _is_meter_qty: 'true'
            };
          }
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body)
          });
          const data = await response.json();
          if (!response.ok || data.status === 422 || data.status) {
            throw new Error(data.description || data.message || 'Cannot add to cart.');
          }
          await refreshCartDrawer();
          openCartDrawer();
        } catch (err) {
          console.error('Add to cart failed:', err);
          showToast(err.message);
        } finally {
          const isOOS = btn && btn.dataset.available === 'false';
          if (!isOOS && btn) {
            btn.disabled = false;
            btn.textContent = '';
            btn.insertAdjacentHTML('beforeend', '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg> <span>Add to Cart</span>');
          }
        }
      });
    }

    // Handle product card Add to Cart buttons
    document.querySelectorAll('.ajax-cart-form').forEach(cartForm => {
      cartForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = cartForm.querySelector('button[type="submit"]');
        const variantId = cartForm.querySelector('input[name="id"]')?.value;
        const qty = parseInt(cartForm.querySelector('input[name="quantity"]')?.value || '1', 10);
        const isMeter = cartForm.querySelector('input[name="properties[_is_meter_qty]"]')?.value === 'true';

        if (!variantId) return;

        if (btn) {
          btn.disabled = true;
          btn.dataset.originalHtml = btn.innerHTML;
          btn.innerHTML = '<svg class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>';
        }

        try {
          const body = { id: variantId, quantity: qty };
          if (isMeter) {
            body.properties = {
              _is_meter_qty: 'true'
            };
          }
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body)
          });
          const data = await response.json();
          if (!response.ok || data.status === 422 || data.status) {
            throw new Error(data.description || data.message || 'Cannot add to cart.');
          }
          await refreshCartDrawer();
          openCartDrawer();
        } catch (err) {
          console.error('Add to cart failed:', err);
          showToast(err.message);
        } finally {
          if (btn) {
            btn.disabled = false;
            if (btn.dataset.originalHtml) {
              btn.innerHTML = btn.dataset.originalHtml;
            }
          }
        }
      });
    });
  }

  /* ════════════════════════════════════════
     Cart Drawer
  ════════════════════════════════════════ */
  async function refreshCartDrawer() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      renderCartDrawer(cart);
    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }

  function renderCartDrawer(cart) {
    const itemsEl = document.getElementById('cart-drawer-items');
    const emptyEl = document.getElementById('cart-drawer-empty');
    const countEls = document.querySelectorAll('#cart-drawer-count');
    const subtotalEl = document.getElementById('cart-drawer-subtotal');
    const checkoutBtn = document.getElementById('cart-drawer-checkout-btn');

    if (!itemsEl) return;

    // Update all count badges (header + drawer)
    countEls.forEach(el => {
      el.textContent = cart.item_count;
      el.classList.toggle('hidden', cart.item_count === 0);
    });

    // Update notification dot
    document.querySelectorAll('#cart-drawer-dot').forEach(el => {
      el.classList.toggle('hidden', cart.item_count === 0);
    });
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (cart.item_count === 0) {
      if (emptyEl) emptyEl.style.display = 'flex';
      itemsEl.querySelectorAll('.cart-line-item').forEach(el => el.remove());
      if (checkoutBtn) {
        checkoutBtn.classList.add('pointer-events-none', 'opacity-50');
      }
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (checkoutBtn) {
      checkoutBtn.classList.remove('pointer-events-none', 'opacity-50');
    }
    itemsEl.querySelectorAll('.cart-line-item').forEach(el => el.remove());

    cart.items.forEach(item => {
      const isMeter = item.properties && item.properties['_is_meter_qty'] === 'true';
      const metersRaw = item.properties && item.properties['_meters'];
      const meters = metersRaw ? parseFloat(metersRaw) : unitsToMeters(item.quantity);
      const pricePerMeter = isMeter ? item.price * UNITS_PER_METER : item.price;

      const qtyDisplay = isMeter ? `${formatMetersDisplay(meters)} m` : item.quantity;
      const priceDisplay = isMeter ? `${formatMoney(pricePerMeter)} / m` : formatMoney(item.price);

      const div = document.createElement('div');
      div.className = 'cart-line-item flex gap-4 items-start p-4 bg-gray-50 rounded-2xl';
      div.dataset.key = item.key;
      div.dataset.isMeter = isMeter;
      div.dataset.currentUnits = item.quantity;
      div.dataset.minUnits = isMeter ? UNITS_PER_METER : 1; // minimum units for this item type

      div.insertAdjacentHTML('beforeend', `
        <div class="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200">
          ${item.image
          ? `<img src="${item.image}" alt="${item.title}" class="w-full h-full object-cover">`
          : '<div class="w-full h-full bg-gray-300"></div>'}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-gray-900 truncate">${item.title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="text-xs text-gray-400">${item.variant_title}</p>`
          : ''}
          <p class="text-xs text-gray-500 mt-0.5">${priceDisplay}</p>
          <div class="flex items-center gap-3 mt-2">
            <div class="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1">
              <button
                class="cart-qty-minus text-gray-500 hover:text-brand-pink font-bold text-base leading-none cursor-pointer"
                data-key="${item.key}"
                data-unit-price="${item.price}"
                aria-label="Decrease quantity"
              >−</button>
              <span class="text-sm font-bold text-gray-900 min-w-[44px] text-center cart-qty-label">${qtyDisplay}</span>
              <button
                class="cart-qty-plus text-gray-500 hover:text-brand-pink font-bold text-base leading-none cursor-pointer"
                data-key="${item.key}"
                data-unit-price="${item.price}"
                aria-label="Increase quantity"
              >+</button>
            </div>
          </div>
        </div>
        <div class="text-right flex-shrink-0">
          <p class="text-sm font-bold text-gray-900">${formatMoney(item.line_price)}</p>
          <button
            class="cart-remove text-xs text-red-400 hover:text-red-600 mt-1 transition"
            data-key="${item.key}"
          >Remove</button>
        </div>
      `);

      itemsEl.appendChild(div);
    });

    bindCartControls();
  }

  function bindCartControls() {
    /* ─── +/- for all items ─── */
    document.querySelectorAll('.cart-qty-plus').forEach(btn => {
      btn.onclick = async (e) => {
        e.preventDefault();
        const key = btn.dataset.key;
        const lineDiv = btn.closest('.cart-line-item');
        const isMeter = lineDiv.dataset.isMeter === 'true';
        const current = parseInt(lineDiv.dataset.currentUnits, 10);
        // Meter: step = 1 unit (0.25m); Standard: step = 1 unit
        await updateCartItem(key, current + 1, isMeter);
      };
    });

    document.querySelectorAll('.cart-qty-minus').forEach(btn => {
      btn.onclick = async (e) => {
        e.preventDefault();
        const key = btn.dataset.key;
        const lineDiv = btn.closest('.cart-line-item');
        const isMeter = lineDiv.dataset.isMeter === 'true';
        const minUnits = parseInt(lineDiv.dataset.minUnits, 10) || 1;
        const current = parseInt(lineDiv.dataset.currentUnits, 10);
        if (current <= minUnits) return; // already at minimum
        await updateCartItem(key, current - 1, isMeter);
      };
    });

    /* ─── Remove ─── */
    document.querySelectorAll('.cart-remove').forEach(btn => {
      btn.onclick = async (e) => {
        e.preventDefault();
        // Optimistic: dim the item
        const lineDiv = btn.closest('.cart-line-item');
        if (lineDiv) lineDiv.style.opacity = '0.4';
        await updateCartItem(btn.dataset.key, 0, false);
      };
    });
  }

  async function updateCartItem(key, qty, isMeter) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      const cart = await res.json();

      if (!res.ok || cart.status === 422 || cart.status) {
        let errorMsg = cart.description || cart.message || 'Cannot update cart.';
        if (isMeter && errorMsg) {
          const numMatch = errorMsg.match(/\b\d+\b/);
          if (numMatch) {
            const units = parseInt(numMatch[0], 10);
            errorMsg = `Only ${units / 4} m available in stock.`;
          } else {
            errorMsg = 'Maximum available meters reached.';
          }
        }
        throw new Error(errorMsg);
      }

      renderCartDrawer(cart);
    } catch (err) {
      console.error('Cart update failed:', err);
      showToast(err.message);
      // Refresh the drawer to snap back the quantity to the true server state
      await refreshCartDrawer();
    }
  }

  /* ─── Drawer open/close ─── */
  window.openCartDrawer = function () {
    const panel = document.getElementById('cart-drawer-panel');
    const backdrop = document.getElementById('cart-drawer-backdrop');
    const drawer = document.getElementById('cart-drawer');
    if (!panel) return;

    // Prevent page jumping from scrollbar removal
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      // Check for sticky/fixed elements like header that might need compensation too
      const header = document.querySelector('header.sticky, header.fixed, .sticky-header');
      if (header) {
        header.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    drawer.classList.remove('pointer-events-none');
    backdrop.classList.remove('pointer-events-none', 'opacity-0');
    backdrop.classList.add('opacity-100', 'pointer-events-auto');
    panel.classList.remove('translate-x-full');
    panel.classList.add('translate-x-0');
    document.body.classList.add('overflow-hidden');
  };

  window.closeCartDrawer = function () {
    const panel = document.getElementById('cart-drawer-panel');
    const backdrop = document.getElementById('cart-drawer-backdrop');
    const drawer = document.getElementById('cart-drawer');
    if (!panel) return;
    panel.classList.add('translate-x-full');
    panel.classList.remove('translate-x-0');
    backdrop.classList.remove('opacity-100', 'pointer-events-auto');
    backdrop.classList.add('opacity-0', 'pointer-events-none');
    document.body.classList.remove('overflow-hidden');

    // Remove scrollbar compensation
    document.body.style.paddingRight = '';
    const header = document.querySelector('header.sticky, header.fixed, .sticky-header');
    if (header) {
      header.style.paddingRight = '';
    }

    setTimeout(() => drawer.classList.add('pointer-events-none'), 300);
  };

  /* ─── Cart icon binding ─── */
  function bindCartIcon() {
    document.querySelectorAll('[data-cart-trigger], .cart-icon-btn').forEach(el => {
      el.addEventListener('click', async (e) => {
        e.preventDefault();
        openCartDrawer();
        await refreshCartDrawer();
      });
    });
  }

  /* ─── Init on DOM ready ─── */
  document.addEventListener('DOMContentLoaded', () => {
    initPDPMeterSelector();
    initStdQtySelector();
    initAjaxAddToCart();
    bindCartIcon();
    refreshCartDrawer(); // load cart count on every page
  });
})();
